import { useCallback, memo } from 'react';
import { useSnapshot } from 'valtio';

import { appStore, eventsStore, videoStore, spacecraftStore, uiStore, poiStore, gmapStore, nisarStore, stellarStore, layersStore } from '../managers/globalState';
import globalRefs from '../managers/globalRefs';

import '../../www/assets/css/back_button.css';
import BackButtonSVG from '../../www/assets/images/arrow-back-1-1.svg';
import { useParams, useLocation } from 'react-router-dom';
import { getSpacecraftInstrumentData } from '../helpers/tools';

const STELLAR_PARENT_ROUTE_OVERRIDES = {
	'134340_pluto_barycenter': '134340_pluto'
};

const getStellarBackTarget = stellar => {
	if (!stellar?.isMoon) {
		return null;
	}

	const parentId = STELLAR_PARENT_ROUTE_OVERRIDES[stellar.planet] ?? stellar.planet;

	// Pluto is the top-level page for the Pluto system in the UI.
	if (stellar.id === parentId) {
		return null;
	}

	return parentId;
};


const BackButton = () => {
	const { eventYearParam } = useParams();
	const { isMobile } = useSnapshot(appStore.state);
	const { currentEvent, eventsVisibleOnGlobe } = useSnapshot(eventsStore.state);
	const { currentVideo } = useSnapshot(videoStore.state);
	const { spacecraftId } = useSnapshot(spacecraftStore.state);
	const { dem } = useSnapshot(layersStore.state);
	const { currentPoi } = useSnapshot(poiStore.state);
	const { currentGmap } = useSnapshot(gmapStore.state);
	const { currentNisar } = useSnapshot(nisarStore.state);
	const { currentStellar } = useSnapshot(stellarStore.state);

	// Determine if this is the first location in the history.
	const { state } = useLocation();
	const { notFirstPage } = state ?? {};

	const isVisible = dem || notFirstPage && Boolean(eventYearParam || currentEvent || spacecraftId || currentVideo || currentGmap || currentNisar || currentPoi || currentStellar);
	uiStore.setGlobalState({ isBackButtonVisible: Boolean(isVisible) });
	const floorButtonClass = eventsVisibleOnGlobe && !isMobile ? 'floored' : '';
	const bottomPaddingMobile = eventsVisibleOnGlobe ? 'lower-padding' : '';

	const { isInstrument, parentOfInstrument } = (spacecraftId && getSpacecraftInstrumentData(spacecraftId)) || {};

	/**
	 * By default, the back button will go back one in history (like the browser back button).
	 * We can put any custom exceptions here.
	 */
	const clickHandle = useCallback(() => {
		const { getManager } = globalRefs;
		const routeManager = getManager('route');
		const stellarBackTarget = getStellarBackTarget(currentStellar);
		if (dem) {
			layersStore.setGlobalState({ dem: false });
			uiStore.setGlobalState({ overlayType: 'gmap' });
		} else if (isInstrument && parentOfInstrument) {
			// If we're on an instrument, go back to the parent.
			routeManager.navigateToSpacecraft(parentOfInstrument);
		} else if (currentStellar && stellarBackTarget) {
			routeManager.navigateToStellarBody(stellarBackTarget);
		} else if (currentGmap || currentNisar) {
			uiStore.setGlobalState({ overlayType: 'gmap' });
			routeManager.navigateToGmapOverview();
		} else if (currentPoi) {
			uiStore.setGlobalState({ overlayType: 'poi' });
		} else if (currentStellar) {
			routeManager.goHome();
		} else if (notFirstPage) {
			routeManager.goBack();
		} else {
			routeManager.goHome();
		}
	}, [
		dem,
		isInstrument,
		parentOfInstrument,
		currentStellar,
		currentGmap,
		currentNisar,
		notFirstPage,
		currentPoi
	]);

	return isVisible && (
		<button className={`back-btn ${floorButtonClass} ${bottomPaddingMobile}`} onClick={clickHandle}>
			<BackButtonSVG />
			رجوع
		</button>
	);
};


export default memo(BackButton);
